// WHAT THE TRACKING PAGE IS ALLOWED TO BUILD.
//
// Owner, 2026-09-25: unticking a sub-category in the right panel means
// its elements "should be not included in all loading ... it's like
// never selected from model source during source selection initially".
// Option 1 as he chose it: the rows are never BUILT, never DRAWN and
// never held in memory. The fragment bytes still arrive, because a
// fragment can be referenced by other tiles and forcing them not to
// download is work he has deferred.
//
// THIS IS THE ONLY SAFE GRANULARITY. Skipping a FRAGMENT would delete
// geometry from still-ticked elements - fragments are shared, which is
// what instancing means. Skipping an INSTANCE ROW removes one element
// and is invisible to every other.
//
// IT IS A COMMITTED SCOPE, NOT A LIVE ONE. Ticks are pending until the
// owner presses Save, so this changes ONCE per save rather than on every
// click - the loader is never re-planning a stream under somebody's
// mouse.
//
// ABSENT MEANS NO FILTER, NOT AN EMPTY ONE. A page that has not told us
// its scope gets the whole model; only an explicit set excludes
// anything. The opposite default would make a failed scope fetch look
// exactly like "everything is unticked", and a viewer that silently
// drew nothing would be indistinguishable from a broken one.

export function makeScopeFilter(excludedUids) {
  if (!excludedUids) return null;                       // not told: build everything
  const set = excludedUids instanceof Set ? excludedUids : new Set(excludedUids);
  if (!set.size) return null;                           // told, and nothing is excluded
  return {
    size: set.size,
    // `true` means this row may be built.
    allows(uid) { return !set.has(uid); },
    excludes(uid) { return set.has(uid); },
  };
}

// Drop the rows a scope excludes. Returns the SAME array when nothing is
// filtered, so the common case costs one comparison and no allocation -
// this runs for every tile that streams in.
export function applyScopeFilter(rows, filter) {
  if (!filter || !Array.isArray(rows) || !rows.length) return rows;
  let drop = 0;
  for (const r of rows) if (r && filter.excludes(r.uniqueId)) drop += 1;
  if (!drop) return rows;
  const out = [];
  for (const r of rows) if (!r || filter.allows(r.uniqueId)) out.push(r);
  return out;
}

// What a page can report without walking the model: how many of a tile's
// rows this scope removed. Counted rather than inferred from a
// difference, so "nothing was excluded" and "this tile was empty" stay
// distinguishable.
export function countExcluded(rows, filter) {
  if (!filter || !Array.isArray(rows)) return 0;
  let n = 0;
  for (const r of rows) if (r && filter.excludes(r.uniqueId)) n += 1;
  return n;
}

// THE COMMITTED SCOPE, held for the loader the way nestedParts holds its
// parent map: the page sets it once per SAVE, and every tile normalised
// afterwards - including ones that stream in minutes later - is filtered
// by it. Cleared on teardown so a second project cannot inherit the
// first one's scope.
let current = null;

// WHICH ASK THE COMMITTED SCOPE ANSWERS, and which ask the loader has
// actually FILTERED UNDER. They are different facts and the difference is
// the point (HAL, 2026-09-25): if the page reports "applied" from its own
// state, it is reporting its BELIEF about the viewer. The viewer is the
// only thing that can say it drew under a scope.
//
// `appliedGeneration` is therefore written by the ADMISSION PATH below -
// the code that does the filtering - and never by the page.
let committedGeneration = null;
let appliedGeneration = null;
const appliedListeners = new Set();

function announceApplied() {
  // A listener must never be able to stop a tile being normalised.
  for (const cb of appliedListeners) {
    try { cb(appliedGeneration); } catch (e) { /* the model still draws */ }
  }
}

// `generation` is optional, and omitting it keeps the old behaviour
// exactly: null stays equal to null, so nothing is ever announced for a
// caller that has not opted in.
export function setTrackingScope(excludedUids, generation = null) {
  current = makeScopeFilter(excludedUids);
  committedGeneration = generation;
  appliedGeneration = null;        // committed is not applied
  return current;
}

export function currentTrackingScope() {
  return current;
}

export function forgetTrackingScope() {
  current = null;
  committedGeneration = null;
  const had = appliedGeneration;
  appliedGeneration = null;
  // A teardown means nothing is applied any more, and anyone showing an
  // "applied" state has to hear that rather than keep a stale yes.
  if (had !== null) announceApplied();
}

// The generation the LOADER has filtered under - not the one the page
// committed. Null until a tile has actually been normalised.
export function appliedScopeGeneration() {
  return appliedGeneration;
}

export function onScopeApplied(cb) {
  appliedListeners.add(cb);
  return () => { appliedListeners.delete(cb); };
}

// Is this row outside the committed scope? Shaped like the other two
// admission checks in TileRegistry so it reads as one of a family.
//
// THIS IS ALSO THE ACKNOWLEDGEMENT, and deliberately so: being consulted
// is the only moment anything can honestly say the committed scope
// reached the loader. Recorded on CONSULTATION rather than on a rejection,
// because a scope that excludes everything must still count as applied -
// and on entry rather than after the `current` guard, because drawing
// UNFILTERED after a degradation is a decision that was also applied.
export function isInstanceOutOfScope(inst) {
  if (appliedGeneration !== committedGeneration) {
    appliedGeneration = committedGeneration;
    announceApplied();
  }
  return !!(current && inst && current.excludes(inst.u));
}

// ===========================================================================
// READING THE SERVER'S ANSWER
//
// Shape verified in back/utils/treeUids.js compactUids, not from the
// message describing it: a uid is split at index 36, so the key is the
// first 36 characters WITHOUT the dash and the value is everything from
// index 37 - and the values are COMMA-JOINED STRINGS, not arrays.
//
// That last part is the third time today a comma-joined string has been
// mistaken for a list (NestedComponentIds was the first). Reading it as
// an array gives a PLAUSIBLE wrong answer - one id per prefix - that
// agrees with what anyone expects, and here it would mean almost
// nothing is excluded while the panel says it was. So the test that
// guards it uses TWO ids in one prefix; a fixture with one passes
// either way and proves nothing.
const PREFIX_LEN = 36;

export function expandCompactUids(payload) {
  const out = new Set();
  if (!payload) return out;
  const byPrefix = payload.byPrefix || {};
  for (const prefix of Object.keys(byPrefix)) {
    const joined = byPrefix[prefix];
    if (typeof joined !== 'string' || !joined) continue;
    for (const suffix of joined.split(',')) {
      if (suffix) out.add(`${prefix}-${suffix}`);
    }
  }
  for (const u of (payload.other || [])) if (u) out.add(String(u));
  return out;
}

// ONE DEGRADATION, THREE CAUSES, ONE SENTENCE: refused above the limit,
// refused on a timeout, or an answer computed for a DIFFERENT commit.
// In every one of them the model is drawn UNFILTERED and the page SAYS
// the scope could not be applied.
//
// Never the other two answers: drawing everything SILENTLY shows
// elements the owner believes he removed, and drawing nothing is a
// blank viewer that looks like a broken model.
//
// count === 0 WITHOUT a refusal is a real answer - nothing is excluded -
// and must not be confused with a refusal, which carries count null.
const CANNOT_APPLY = 'The tracking scope could not be applied, so the whole model is shown.';

export function scopeFromResponse(payload, drawingCommitId) {
  if (!payload) return { filter: null, excluded: null, note: null };  // not told: no scope, no complaint
  if (payload.refused) {
    return { filter: null, excluded: null, note: payload.message || CANNOT_APPLY, reason: payload.reason || 'refused' };
  }
  const answeredFor = payload.commitId ? String(payload.commitId) : null;
  const drawing = drawingCommitId ? String(drawingCommitId) : null;
  if (answeredFor && drawing && answeredFor !== drawing) {
    // Yesterday's unticks applied to today's model is the quiet kind of
    // wrong: the elements that vanish look like a publish that lost them.
    return { filter: null, excluded: null, note: CANNOT_APPLY, reason: 'stale_commit' };
  }
  const set = expandCompactUids(payload);
  // The SET travels too, because the caller has to hand it to
  // setTrackingScope - handing back only the filter tempts a caller into
  // passing the raw payload instead, and `new Set(payload)` on a plain
  // object throws rather than failing quietly. Found by writing the
  // wiring against my own return shape.
  return { filter: makeScopeFilter(set), excluded: set, note: null, reason: null };
}

export const SCOPE_CANNOT_APPLY = CANNOT_APPLY;
